import { useEffect } from 'react'
import type { Theme } from '@mui/material/styles'
import type { SystemStyleObject } from '@mui/system'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Slide from '@mui/material/Slide'

import { isTrustedHTML } from '../../../types/utils'
import theme from '../../../theme'
import texts from '../../texts'
import styles from './banner.styles'
import type { NavigationItem } from '../../../types/model'
import { getNavButtonId } from '../BottomNavigation'
import { useRefWithCallback } from '../hooks'

const renderArrow = (
  buttonIcon: NavigationItem,
  bannerMessageElement: HTMLDivElement,
  arrowElement: HTMLDivElement,
) => {
  const buttonId = getNavButtonId(buttonIcon)
  const buttonElement = document.getElementById(buttonId) as HTMLButtonElement
  const buttonBoundingClientRect = buttonElement.getBoundingClientRect()

  const bannerMessageBoundingClientRect =
    bannerMessageElement.getBoundingClientRect()

  const arrowXposition =
    buttonBoundingClientRect.x + buttonBoundingClientRect.width / 2
  const arrowDefaultXPosition =
    bannerMessageBoundingClientRect.x +
    bannerMessageBoundingClientRect.width / 2
  const arrowXTranslateValue = arrowXposition - arrowDefaultXPosition

  arrowElement.classList.remove('hidden')
  arrowElement.style.setProperty(
    'transform',
    `translate(${arrowXTranslateValue.toString()}px, -1px)`,
  )
}

interface BannerProps {
  containerRef: Element | null
  isVisible: boolean
  message?: string | TrustedHTML
  iconButton?: NavigationItem
  onClick?: () => void
  onShow?: () => void
  buttonLabel?: string
  sx?: SystemStyleObject<Theme>
}

const Banner = ({
  containerRef,
  isVisible,
  message = '',
  iconButton,
  onClick = () => {},
  onShow = () => {},
  buttonLabel = texts.okLabel,
  sx = {},
}: BannerProps) => {
  const [_bannerBoxRef, setBannerBoxRef, bannerBoxNode] =
    useRefWithCallback<HTMLDivElement>()
  const [_arrowRef, setArrowRef, arrowNode] =
    useRefWithCallback<HTMLDivElement>()

  useEffect(() => {
    if (!bannerBoxNode || !arrowNode || !iconButton) return
    const observer = new ResizeObserver(() => {
      renderArrow(iconButton, bannerBoxNode, arrowNode)
    })
    observer.observe(bannerBoxNode)
    return () => observer.disconnect()
  }, [bannerBoxNode, arrowNode, iconButton])

  useEffect(() => {
    if (isVisible) {
      onShow()
    }
  }, [isVisible])

  return (
    <Slide
      in={isVisible}
      direction='up'
      mountOnEnter
      unmountOnExit
      container={containerRef}
      timeout={1000}
      easing={theme.transitions.easing.easeOut}
    >
      <Box sx={[styles.root, sx]}>
        <Box sx={styles.bannerBox} ref={setBannerBoxRef}>
          {isTrustedHTML(message) ? (
            <Typography
              dangerouslySetInnerHTML={{
                __html: message as TrustedHTML,
              }}
            />
          ) : (
            <Typography>{message}</Typography>
          )}
          <Button id='p-banner-button' onClick={onClick}>
            {buttonLabel}
          </Button>
        </Box>
        <Box id='p-banner-arrow-anchor' sx={styles.arrowAnchor}>
          <Box ref={setArrowRef} className='hidden'></Box>
        </Box>
      </Box>
    </Slide>
  )
}

export default Banner
